import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { loadRemote } from '@module-federation/runtime';
import { useAppRegistry } from '../api/endpoints/useAppRegistry';
import { RoutePath } from '../types/categories';
import { APP_NAME_PATTERN } from '../utils/customApp';
import { resolveAppConfig } from '../utils/resolveAppConfig';
import { ensureRemoteRegistered } from './remoteRegistration';

const CUSTOM_APP_ROUTE = `/clientId/:clientId${RoutePath.APPS}/:appName/*`;

/**
 * Starts loading the custom app's remote as soon as the route and the app
 * registry are known, instead of when `CustomAppPage` mounts.
 *
 * The page itself only mounts once `App` renders its routes, which waits on the
 * locale catalog (`useConfigureLocale`). On a slow connection that is several
 * seconds in which the remote could already be downloading. The remote is
 * resolved exactly as `CustomAppPage` resolves it — same registry lookup
 * (aliases included), same `resolveAppConfig`, same registration — so this
 * only moves the load earlier. The federation runtime memoises `loadRemote`
 * per module, so the page's own `loadRemote` reuses this result, and a failure
 * here surfaces there exactly as it would have without the preload.
 */
export function useCustomAppPreload(clientId: string): void {
  const { pathname } = useLocation();
  const { remoteAppFor, isReady } = useAppRegistry(clientId);
  const appName = matchPath(CUSTOM_APP_ROUTE, pathname)?.params.appName;

  useEffect(() => {
    if (!isReady || !clientId.trim() || !appName) {
      return;
    }
    if (!APP_NAME_PATTERN.test(appName)) {
      return;
    }
    const config = resolveAppConfig(
      { name: appName, appName },
      appName,
      clientId,
      remoteAppFor(appName)
    );
    if (!config) {
      return;
    }
    ensureRemoteRegistered(config.remoteName, config.remoteEntry);
    // The page reports the failure when it mounts and calls loadRemote itself.
    loadRemote(config.remoteModule).catch(() => undefined);
  }, [isReady, clientId, appName, remoteAppFor]);
}
